import { useState, useEffect } from 'react';
import AuthGuard from '@/components/feature/AuthGuard';
import AdminLayout from './components/AdminLayout';
import { supabase } from '@/lib/supabase';

interface KPI {
  totalTeams: number;
  totalParticipants: number;
  totalFacultyMentors: number;
  totalColleges: number;
  totalCities: number;
  totalRevenue: number;
  paidTeams: number;
  pendingPayments: number;
  failedPayments: number;
}

function CountdownBadge({ targetDate, label }: { targetDate: string; label: string }) {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const calc = () => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const diff = target - now;
      if (diff <= 0) { setTimeLeft('Event has started!'); return; }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      setTimeLeft(`${days}d ${hours}h ${mins}m`);
    };
    calc();
    const interval = setInterval(calc, 60000);
    return () => clearInterval(interval);
  }, [targetDate]);

  return (
    <div className="p-5 rounded-xl border border-background-200/70 bg-background-50">
      <p className="text-xs text-foreground-500 mb-2">{label}</p>
      <p className="text-xl font-bold text-foreground-900 font-heading">{timeLeft}</p>
    </div>
  );
}

function SimpleBarChart({ data, dataKey, labelKey, color, height }: {
  data: { [key: string]: string | number }[];
  dataKey: string;
  labelKey: string;
  color: string;
  height: number;
}) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <p className="text-sm text-foreground-400">No data available yet</p>
      </div>
    );
  }

  const maxVal = Math.max(...data.map((d) => Number(d[dataKey])), 1);
  const barWidth = Math.max(8, Math.min(28, (800 / data.length) - 4));
  const gap = 4;

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${data.length * (barWidth + gap) + 20} ${height}`} preserveAspectRatio="xMidYMid meet">
      {data.map((d, i) => {
        const val = Number(d[dataKey]);
        const barHeight = (val / maxVal) * (height - 40);
        const x = i * (barWidth + gap) + 10;
        const y = height - barHeight - 25;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barWidth} height={barHeight} rx="3" fill={color} opacity="0.85">
              <title>{`${d[labelKey]}: ${val}`}</title>
            </rect>
            <text x={x + barWidth / 2} y={height - 5} textAnchor="middle" className="text-[9px]" fill="oklch(var(--foreground-400))" fontFamily="var(--font-body)">
              {String(d[labelKey]).length > 5 ? String(d[labelKey]).slice(0, 5) + '..' : d[labelKey]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function SimpleLineChart({ data, dataKey, labelKey, color, height }: {
  data: { [key: string]: string | number }[];
  dataKey: string;
  labelKey: string;
  color: string;
  height: number;
}) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <p className="text-sm text-foreground-400">No data available yet</p>
      </div>
    );
  }

  const maxVal = Math.max(...data.map((d) => Number(d[dataKey])), 1);
  const w = data.length > 1 ? 760 : 300;
  const padX = 40;
  const padY = 25;
  const chartW = w - padX * 2;
  const chartH = height - padY * 2;

  const points = data.map((d, i) => {
    const x = padX + (i / (data.length - 1 || 1)) * chartW;
    const y = padY + chartH - (Number(d[dataKey]) / maxVal) * chartH;
    return { x, y, label: String(d[labelKey]), val: Number(d[dataKey]) };
  });

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaD = `${pathD} L ${points[points.length - 1].x} ${padY + chartH} L ${points[0].x} ${padY + chartH} Z`;

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={areaD} fill="url(#lineGrad)" />
      <path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="3" fill={color} />
          <text x={p.x} y={padY + chartH + 15} textAnchor="middle" className="text-[9px]" fill="oklch(var(--foreground-400))" fontFamily="var(--font-body)">
            {p.label}
          </text>
        </g>
      ))}
    </svg>
  );
}

function HorizontalBarChart({ data, dataKey, labelKey, color, height }: {
  data: { [key: string]: string | number }[];
  dataKey: string;
  labelKey: string;
  color: string;
  height: number;
}) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <p className="text-sm text-foreground-400">No data available yet</p>
      </div>
    );
  }

  const maxVal = Math.max(...data.map((d) => Number(d[dataKey])), 1);
  const barH = 24;
  const gap = 8;
  const totalH = data.length * (barH + gap) + 40;
  const labelW = 100;

  return (
    <svg width="100%" height={Math.max(height, totalH)} viewBox={`0 0 500 ${totalH}`} preserveAspectRatio="xMidYMid meet">
      {data.map((d, i) => {
        const val = Number(d[dataKey]);
        const barW = (val / maxVal) * 320;
        const y = i * (barH + gap) + 20;
        return (
          <g key={i}>
            <text x={labelW - 6} y={y + barH / 2 + 4} textAnchor="end" className="text-[10px]" fill="oklch(var(--foreground-500))" fontFamily="var(--font-body)">
              {String(d[labelKey]).slice(0, 12)}
            </text>
            <rect x={labelW} y={y} width={Math.max(barW, 4)} height={barH} rx="4" fill={color} opacity="0.8">
              <title>{`${d[labelKey]}: ${val}`}</title>
            </rect>
            <text x={labelW + Math.max(barW, 4) + 6} y={y + barH / 2 + 4} className="text-[10px] font-semibold" fill="oklch(var(--foreground-700))" fontFamily="var(--font-body)">
              {val}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export default function AdminDashboard() {
  const [kpi, setKPI] = useState<KPI>({
    totalTeams: 0, totalParticipants: 0, totalFacultyMentors: 0,
    totalColleges: 0, totalCities: 0, totalRevenue: 0,
    paidTeams: 0, pendingPayments: 0, failedPayments: 0,
  });
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  // Chart data from Supabase
  const [dailyRegistrations, setDailyRegistrations] = useState<{ date: string; count: number }[]>([]);
  const [revenueTrend, setRevenueTrend] = useState<{ date: string; amount: number }[]>([]);
  const [collegeDistribution, setCollegeDistribution] = useState<{ name: string; count: number }[]>([]);
  const [departmentDistribution, setDepartmentDistribution] = useState<{ name: string; count: number }[]>([]);
  const [cityDistribution, setCityDistribution] = useState<{ name: string; count: number }[]>([]);
  const [yearDistribution, setYearDistribution] = useState<{ name: string; count: number }[]>([]);

  useEffect(() => {
    async function fetchAll() {
      setLoading(true);
      setFetchError('');
      try {
        // KPI: teams
        const { data: teams, error: teamsErr } = await supabase.from('teams').select('id, college_name, city, status, created_at');
        if (teamsErr) throw teamsErr;

        const { data: participants, error: partErr } = await supabase.from('participants').select('id, year_of_study');
        if (partErr) throw partErr;

        const { data: mentors, error: mentErr } = await supabase.from('faculty_mentors').select('id');
        if (mentErr) throw mentErr;

        const { data: payments, error: payErr } = await supabase.from('payments').select('id, amount, status');
        if (payErr) throw payErr;

        const teamsArr = teams || [];
        const partsArr = participants || [];
        const mentsArr = mentors || [];
        const paysArr = payments || [];

        // KPI calculations
        const collegesSet = new Set(teamsArr.map((t) => t.college_name));
        const citiesSet = new Set(teamsArr.map((t) => t.city));

        setKPI({
          totalTeams: teamsArr.length,
          totalParticipants: partsArr.length,
          totalFacultyMentors: mentsArr.length,
          totalColleges: collegesSet.size,
          totalCities: citiesSet.size,
          totalRevenue: paysArr.filter((p) => p.status === 'paid').reduce((s, p) => s + Number(p.amount), 0),
          paidTeams: paysArr.filter((p) => p.status === 'paid').length,
          pendingPayments: paysArr.filter((p) => p.status === 'pending').length,
          failedPayments: paysArr.filter((p) => p.status === 'failed').length,
        });

        // Daily registrations chart
        const dateCounts: Record<string, number> = {};
        teamsArr.forEach((t) => {
          const d = t.created_at ? new Date(t.created_at).toISOString().split('T')[0] : 'unknown';
          dateCounts[d] = (dateCounts[d] || 0) + 1;
        });
        const dailyData = Object.entries(dateCounts)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([date, count]) => ({ date: date.slice(5), count }));
        setDailyRegistrations(dailyData);

        // Revenue trend
        const revCounts: Record<string, number> = {};
        paysArr.filter((p) => p.status === 'paid').forEach((p) => {
          const d = p.created_at ? new Date(p.created_at).toISOString().split('T')[0] : 'unknown';
          revCounts[d] = (revCounts[d] || 0) + Number(p.amount);
        });
        const revData = Object.entries(revCounts)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([date, amount]) => ({ date: date.slice(5), amount }));
        setRevenueTrend(revData);

        // College distribution
        const colCounts: Record<string, number> = {};
        teamsArr.forEach((t) => { colCounts[t.college_name] = (colCounts[t.college_name] || 0) + 1; });
        setCollegeDistribution(Object.entries(colCounts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count));

        // Department distribution
        const deptCounts: Record<string, number> = {};
        teamsArr.forEach((t) => { deptCounts[t.department] = (deptCounts[t.department] || 0) + 1; });
        setDepartmentDistribution(Object.entries(deptCounts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count));

        // City distribution
        const cityCounts: Record<string, number> = {};
        teamsArr.forEach((t) => { cityCounts[t.city] = (cityCounts[t.city] || 0) + 1; });
        setCityDistribution(Object.entries(cityCounts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count));

        // Year distribution
        const yearCounts: Record<string, number> = {};
        partsArr.forEach((p) => { yearCounts[p.year_of_study] = (yearCounts[p.year_of_study] || 0) + 1; });
        setYearDistribution(Object.entries(yearCounts).map(([name, count]) => ({ name, count })).sort());

      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error('[AdminDashboard] Fetch error:', msg);
        setFetchError(msg);
      } finally {
        setLoading(false);
      }
    }

    fetchAll();
  }, []);

  const kpiCards = [
    { icon: 'ri-file-list-3-line', label: 'Total Teams', value: kpi.totalTeams, color: 'bg-primary-100 text-primary-600' },
    { icon: 'ri-team-line', label: 'Total Participants', value: kpi.totalParticipants, color: 'bg-accent-100 text-accent-600' },
    { icon: 'ri-user-star-line', label: 'Faculty Mentors', value: kpi.totalFacultyMentors, color: 'bg-secondary-100 text-secondary-600' },
    { icon: 'ri-building-line', label: 'Colleges', value: kpi.totalColleges, color: 'bg-primary-100 text-primary-700' },
    { icon: 'ri-map-pin-line', label: 'Cities', value: kpi.totalCities, color: 'bg-accent-100 text-accent-700' },
    { icon: 'ri-money-rupee-circle-line', label: 'Revenue', value: kpi.totalRevenue > 0 ? `₹${kpi.totalRevenue.toLocaleString()}` : '₹0', color: 'bg-secondary-100 text-secondary-700' },
    { icon: 'ri-check-double-line', label: 'Paid Teams', value: kpi.paidTeams, color: 'bg-accent-50 text-accent-600' },
    { icon: 'ri-time-line', label: 'Pending', value: kpi.pendingPayments, color: 'bg-amber-100 text-amber-600' },
    { icon: 'ri-close-circle-line', label: 'Failed', value: kpi.failedPayments, color: 'bg-red-100 text-red-600' },
  ];

  const totalTeams = kpi.totalTeams || 1;
  const paymentRate = totalTeams > 0 ? Math.round((kpi.paidTeams / totalTeams) * 100) : 0;
  const verificationRate = totalTeams > 0 ? Math.round((kpi.paidTeams / totalTeams) * 100) : 0;
  const mentorRate = totalTeams > 0 ? Math.round((kpi.totalFacultyMentors / totalTeams) * 100) : 0;
  const avgTeamSize = kpi.totalTeams > 0 ? (kpi.totalParticipants / kpi.totalTeams).toFixed(1) : '0';

  return (
    <AuthGuard requiredRole="admin">
      <AdminLayout title="Dashboard" breadcrumbs={[{ label: 'Dashboard' }]}>
        {fetchError && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
            Failed to load dashboard data: {fetchError}. <button onClick={() => window.location.reload()} className="underline font-medium cursor-pointer">Retry</button>
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
          {kpiCards.map((card) => (
            <div key={card.label} className="p-4 md:p-5 rounded-xl border border-background-200/70 bg-background-50 hover:bg-background-100/50 transition-colors">
              <div className={`w-9 h-9 rounded-lg ${card.color} flex items-center justify-center mb-3`}>
                <i className={`${card.icon} text-base`}></i>
              </div>
              <p className="text-xs text-foreground-500 whitespace-nowrap">{card.label}</p>
              <p className="text-xl md:text-2xl font-bold text-foreground-900 mt-0.5">
                {loading ? <span className="inline-block w-12 h-5 rounded bg-background-200 animate-pulse"></span> : card.value}
              </p>
            </div>
          ))}
        </div>

        {/* Countdowns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <CountdownBadge targetDate="2026-09-01T23:59:59" label="Registration Deadline — 1 September 2026" />
          <CountdownBadge targetDate="2026-09-25T09:00:00" label="Event Starts — 25 September 2026" />
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
          <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
            <h3 className="font-heading text-sm font-bold text-foreground-950 mb-4">Daily Registrations</h3>
            {loading ? (
              <div className="h-[220px] flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-primary-400 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <SimpleLineChart data={dailyRegistrations} dataKey="count" labelKey="date" color="oklch(var(--primary-500))" height={220} />
            )}
          </div>
          <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
            <h3 className="font-heading text-sm font-bold text-foreground-950 mb-4">Revenue Trend</h3>
            {loading ? (
              <div className="h-[220px] flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-accent-400 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <SimpleLineChart data={revenueTrend} dataKey="amount" labelKey="date" color="oklch(var(--accent-500))" height={220} />
            )}
          </div>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
          <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
            <h3 className="font-heading text-sm font-bold text-foreground-950 mb-4">College-wise Teams</h3>
            {loading ? (
              <div className="h-[280px] flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary-400 border-t-transparent rounded-full animate-spin"></div></div>
            ) : (
              <HorizontalBarChart data={collegeDistribution} dataKey="count" labelKey="name" color="oklch(var(--primary-500))" height={280} />
            )}
          </div>
          <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
            <h3 className="font-heading text-sm font-bold text-foreground-950 mb-4">Department-wise Teams</h3>
            {loading ? (
              <div className="h-[260px] flex items-center justify-center"><div className="w-6 h-6 border-2 border-accent-400 border-t-transparent rounded-full animate-spin"></div></div>
            ) : (
              <HorizontalBarChart data={departmentDistribution} dataKey="count" labelKey="name" color="oklch(var(--accent-500))" height={260} />
            )}
          </div>
        </div>

        {/* Charts Row 3 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
          <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
            <h3 className="font-heading text-sm font-bold text-foreground-950 mb-4">Year-wise Distribution</h3>
            {loading ? (
              <div className="py-6 flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary-400 border-t-transparent rounded-full animate-spin"></div></div>
            ) : yearDistribution.length === 0 ? (
              <div className="py-6 text-center"><p className="text-sm text-foreground-400">No participants yet</p></div>
            ) : (
              <div className="flex items-center justify-center gap-12 py-6">
                {yearDistribution.map((item) => {
                  const totalParts = yearDistribution.reduce((s, i) => s + i.count, 0) || 1;
                  return (
                    <div key={item.name} className="text-center">
                      <div className="relative w-24 h-24 mx-auto mb-3">
                        <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none" stroke="oklch(var(--background-200))" strokeWidth="3"
                          />
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831"
                            fill="none"
                            stroke={item.name === item.name ? 'oklch(var(--primary-500))' : 'oklch(var(--accent-500))'}
                            strokeWidth="3"
                            strokeDasharray={`${(item.count / totalParts) * 100}, 100`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <p className="absolute inset-0 flex items-center justify-center font-heading font-bold text-lg text-foreground-900">{item.count}</p>
                      </div>
                      <p className="text-xs font-medium text-foreground-600">{item.name}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
            <h3 className="font-heading text-sm font-bold text-foreground-950 mb-4">City-wise Distribution</h3>
            {loading ? (
              <div className="h-[220px] flex items-center justify-center"><div className="w-6 h-6 border-2 border-secondary-400 border-t-transparent rounded-full animate-spin"></div></div>
            ) : (
              <HorizontalBarChart data={cityDistribution} dataKey="count" labelKey="name" color="oklch(var(--secondary-500))" height={220} />
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
          <h3 className="font-heading text-sm font-bold text-foreground-950 mb-4">Quick Stats Overview</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-xl bg-background-100/70">
              <p className="text-xs text-foreground-500">Payment Rate</p>
              <p className="text-lg font-bold text-foreground-900">{loading ? '--' : `${paymentRate}%`}</p>
              <div className="mt-2 h-2 rounded-full bg-background-200 overflow-hidden">
                <div className="h-full rounded-full bg-accent-500" style={{ width: `${paymentRate}%` }}></div>
              </div>
            </div>
            <div className="p-4 rounded-xl bg-background-100/70">
              <p className="text-xs text-foreground-500">Verification Rate</p>
              <p className="text-lg font-bold text-foreground-900">{loading ? '--' : `${verificationRate}%`}</p>
              <div className="mt-2 h-2 rounded-full bg-background-200 overflow-hidden">
                <div className="h-full rounded-full bg-primary-500" style={{ width: `${verificationRate}%` }}></div>
              </div>
            </div>
            <div className="p-4 rounded-xl bg-background-100/70">
              <p className="text-xs text-foreground-500">Avg Team Size</p>
              <p className="text-lg font-bold text-foreground-900">{loading ? '--' : avgTeamSize}</p>
            </div>
            <div className="p-4 rounded-xl bg-background-100/70">
              <p className="text-xs text-foreground-500">Mentor Rate</p>
              <p className="text-lg font-bold text-foreground-900">{loading ? '--' : `${mentorRate}%`}</p>
              <div className="mt-2 h-2 rounded-full bg-background-200 overflow-hidden">
                <div className="h-full rounded-full bg-secondary-500" style={{ width: `${mentorRate}%` }}></div>
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    </AuthGuard>
  );
}